import { SparklesIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import Tooltip from 'components/layout/tooltip';
import { SystemOptionType } from 'data/types';
import useIsCompatibilityPathway from 'hooks/use-is-compatibility-pathway';
import { ReactNode, useMemo } from 'react';
import {
  ductedDuplicateNameIndexes,
  numberedDuctedSystemLabel
} from 'utils/duplicate-system-names';
import EditableSystemName from './editable-system-name';
import { PickKind } from './equipment-comparison-utils';
import { SCORE_CATEGORY_DEFINITIONS } from './score-category-labels';
import ScoreCategoryTooltip from './score-category-tooltip';
import ScoreChip from './score-chip';
import ScorePenaltiesBonuses from './score-penalties-bonuses';
import { resolveSystemName } from './system-column-helpers';
import { getSystemConfigurationSummaryLines } from './system-configuration-summary';
import SystemTitleTags from './system-title-tags';

type SystemColumnHeaderProps = Readonly<{
  system: SystemOptionType;
  pickKind: PickKind | null;
  systemNumber: number;
  swapPopover: ReactNode;
  onRemove: () => void;
  recommended?: boolean;
}>;

function ScoreChipsRow({ system }: { system: SystemOptionType }) {
  const { score } = system;
  if (!score) return null;
  const { categoryScores, total } = score;
  return (
    <div className='grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(130px,1fr))]'>
      {SCORE_CATEGORY_DEFINITIONS.map(({ key, label, icon }) => (
        <ScoreCategoryTooltip
          key={key}
          categoryKey={key}
          breakdown={categoryScores[key]?.breakdown}
        >
          <ScoreChip
            label={label}
            value={categoryScores[key]?.score ?? 0}
            icon={icon}
          />
        </ScoreCategoryTooltip>
      ))}
      <ScoreChip
        label='Overall Score'
        value={total}
        emphasis
        icon={SparklesIcon}
      />
    </div>
  );
}

export default function SystemColumnHeader({
  system,
  pickKind,
  systemNumber,
  swapPopover,
  onRemove,
  recommended = false
}: SystemColumnHeaderProps) {
  const developerMode = useAppSelector((state) => state.ui.developerMode);
  const isDucted = useIsCompatibilityPathway();
  const systemsToCompare = useAppSelector(
    (state) => state.systems.systemsToCompare
  );
  const nameIndexes = useMemo(
    () => ductedDuplicateNameIndexes(systemsToCompare),
    [systemsToCompare]
  );

  const configName = system.configuration.name?.trim();
  const showConfigName = !isDucted && Boolean(configName);
  const showTopMatch = pickKind === 'bestOverall';
  const { condenserLine, zoneLine } =
    getSystemConfigurationSummaryLines(system);

  return (
    <div className='flex h-full min-w-0 flex-col gap-4 rounded-t-lg border border-gray-200 bg-white p-4'>
      <div className='flex items-start justify-between gap-2'>
        <div className='min-w-0'>
          {showConfigName && (
            <div className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
              Configuration: {configName}
            </div>
          )}
          <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
            <EditableSystemName
              system={system}
              displayName={numberedDuctedSystemLabel(
                resolveSystemName(system, pickKind, systemNumber),
                system,
                nameIndexes
              )}
              showTopMatch={false}
            />
            <SystemTitleTags
              system={system}
              showTopMatch={showTopMatch}
              recommended={recommended}
            />
          </div>
          {!developerMode && (
            <div className='mt-1 flex flex-col text-sm text-gray-600'>
              <div>{condenserLine}</div>
              <div>{zoneLine}</div>
            </div>
          )}
        </div>
        <div className='flex shrink-0 items-center gap-1.5'>
          {swapPopover}
          <button
            type='button'
            onClick={onRemove}
            aria-label='Remove this system from the comparison'
            className='relative inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md bg-white px-2 py-1 text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-blue-500'
          >
            <TrashIcon className='h-4 w-4' />
            <Tooltip text='Remove from comparison' />
          </button>
        </div>
      </div>

      {developerMode && (
        <>
          <ScoreChipsRow system={system} />
          <ScorePenaltiesBonuses score={system.score} />
        </>
      )}
    </div>
  );
}
